import React, { createContext, useContext, useEffect, useState } from "react";
import * as authApi from "@/lib/adapters/auth";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  useEffect(() => {
    (async () => {
      try {
        const data = await authApi.me();
        setUser(data);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const data = await authApi.login(email, password);
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    return data;
  };

  const register = async (name, email, password) => {
    const data = await authApi.register(name, email, password);
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
    return data;
  };

  const logout = async () => {
    await authApi.logout();
    setUser(null);
  };

  // Re-fetch the current user from /v1/me and update React state. Useful after
  // side-channel token acquisition (e.g. in-checkout OTP verify persists a JWT
  // via localStorage without going through the login/register helpers).
  const refresh = async () => {
    try {
      const data = await authApi.me();
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  };

  const requireAuth = () => {
    if (!user) {
      setAuthMode("login");
      setAuthOpen(true);
      return false;
    }
    return true;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, requireAuth, authOpen, setAuthOpen, authMode, setAuthMode }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
